import { get } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import {
  VERTICAL_ALIGNMENT,
  VERTICAL_ALIGNMENT_ICONS,
} from 'hb-builder/constants/alignment';
import type { VerticalAlignmentPosition } from 'hb-builder/types/settings';
import {
  getStringifiedPadding,
  shorthandParser,
} from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

import InputTitle from '../SettingsDecoration/InputTitle';

interface VerticalItemProps {
  dataTestId: string;
  isActive: boolean;
  onClick: () => void;
  type: VerticalAlignmentPosition;
}

interface VerticalAlignmentProps {
  item: DocumentItem<any>;
  onChange: ({
    id,
    values,
  }: {
    id: string;
    values: { settingKey: string; value: string }[];
  }) => void;
}

const VerticalItem = ({
  dataTestId,
  isActive,
  onClick,
  type,
}: VerticalItemProps) => {
  const Icon = VERTICAL_ALIGNMENT_ICONS[type];

  return (
    <VStyledItem
      data-testid={dataTestId}
      data-isactive={isActive}
      isActive={isActive}
      onClick={onClick}
    >
      <Icon size={20} />
    </VStyledItem>
  );
};

const VerticalAlignment = ({ item, onChange }: VerticalAlignmentProps) => {
  const [value, setValue] = useState<VerticalAlignmentPosition>(
    get(item.attributes, 'verticalAlign'),
  );

  useEffect(() => {
    setValue(get(item.attributes, 'verticalAlign'));
  }, [item.attributes, item.attributes?.verticalAlign]);

  const getPaddingWithAlignment = (
    alignment: VerticalAlignmentPosition,
  ): string => {
    const currentPadding = get(item.attributes, 'containerPadding');

    if (!currentPadding) {
      return '0px';
    }

    const currentPaddingVertical =
      shorthandParser(currentPadding, 'top') +
      shorthandParser(currentPadding, 'bottom');
    const paddingHorizontal = {
      right: shorthandParser(currentPadding, 'right'),
      left: shorthandParser(currentPadding, 'left'),
    };

    switch (alignment) {
      case 'top':
        return getStringifiedPadding({
          top: 0,
          bottom: currentPaddingVertical,
          ...paddingHorizontal,
        });
      case 'bottom':
        return getStringifiedPadding({
          bottom: 0,
          top: currentPaddingVertical,
          ...paddingHorizontal,
        });
      case 'middle':
      default:
        return getStringifiedPadding({
          bottom: currentPaddingVertical / 2,
          top: currentPaddingVertical / 2,
          ...paddingHorizontal,
        });
    }
  };

  const handleChange = ({ value }: { value: VerticalAlignmentPosition }) => {
    setValue(value);
    onChange({
      id: item.id,
      values: [
        {
          settingKey: 'attributes.verticalAlign',
          value,
        },
        {
          settingKey: 'attributes.containerPadding',
          value: getPaddingWithAlignment(value),
        },
      ],
    });
  };

  const handleIsActiveVerticalItem = (
    alignmentValue: VerticalAlignmentPosition,
  ) => {
    const currentPadding = get(item.attributes, 'containerPadding');
    const currentPaddingTop = shorthandParser(currentPadding, 'top');
    const currentPaddingBottom = shorthandParser(currentPadding, 'bottom');

    switch (alignmentValue) {
      case 'top':
        return (
          (currentPaddingTop === 0 && currentPaddingBottom !== 0) ||
          value === alignmentValue
        );
      case 'bottom':
        return (
          (currentPaddingBottom === 0 && currentPaddingTop !== 0) ||
          value === alignmentValue
        );
      case 'middle':
        return (
          (currentPaddingTop > 0 &&
            currentPaddingBottom === currentPaddingTop) ||
          value === alignmentValue
        );
      default:
        return false;
    }
  };

  return (
    <Wrapper>
      <InputTitle
        boxProps={{ mr: 3 }}
        id="vertical-alignment"
        label={__('Vertical Alignment')}
      />
      <WrapperItems>
        {Object.values(VERTICAL_ALIGNMENT).map((alignmentValue) => (
          <VerticalItem
            dataTestId={`builder-settings-alignment-${alignmentValue}`}
            key={alignmentValue}
            isActive={handleIsActiveVerticalItem(alignmentValue)}
            onClick={() => handleChange({ value: alignmentValue })}
            type={alignmentValue}
          />
        ))}
      </WrapperItems>
    </Wrapper>
  );
};

export default VerticalAlignment;

const Wrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
}))``;

const VStyledItem = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop),
  })
  .attrs<{ isActive: boolean }>(({ isActive }) => ({
    bg: isActive ? 'primary' : 'white',
    color: isActive ? 'white' : 'asphalt',
    height: 28,
    justifyContent: 'center',
    p: 1,
    width: 28,
  }))`
  cursor: pointer;
`;

const WrapperItems = styled(Box)`
  border-radius: 3px;
  border: ${({ theme }) => `1px solid ${theme.colors.asphalt}`};
  overflow: hidden;

  > * + * {
    border-left: ${({ theme }) => `1px solid ${theme.colors.asphalt}`};
  }
`;
